import React from 'react';
import { useMobile } from '../../../hooks/useMobile';
import InviteStatusBadge from './InviteStatusBadge';
import { ResponsiveGrid, ResponsiveButton } from '../../common/ResponsiveLayout';
import MobileCard from '../../common/MobileCard';
import { formatInviteExpiration, generateInviteUrl } from '../../../services/inviteService';
import toast from 'react-hot-toast';

/**
 * InviteList Component
 * Displays a list of invites with management actions
 */
const InviteList = ({ 
  invites, 
  loading,
  onView,
  onResend,
  onRevoke,
  onCopyLink,
  // Search and filter props
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  sortBy,
  onSortChange,
  sortOrder,
  onSortOrderChange,
  onClearFilters,
  // Pagination props
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange
}) => {
  const isMobile = useMobile();

  const handleCopyInviteLink = async (invite) => {
    try {
      const inviteUrl = generateInviteUrl(invite.invite_token);
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Invite link copied to clipboard!');
      if (onCopyLink) {
        onCopyLink(invite);
      }
    } catch (err) {
      toast.error('Failed to copy invite link');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionButtons = (invite) => (
    <div className="flex flex-col sm:flex-row gap-2">
      <ResponsiveButton
        onClick={() => onView(invite)}
        variant="secondary"
        size="sm"
        className="text-xs px-4 py-2"
      >
        View
      </ResponsiveButton>
      
      {invite.status === 'pending' && (
        <>
          <ResponsiveButton
            onClick={() => handleCopyInviteLink(invite)}
            variant="secondary"
            size="sm"
            className="text-xs px-4 py-2"
          >
            Copy Link
          </ResponsiveButton>
          
          <ResponsiveButton
            onClick={() => onResend(invite)}
            variant="secondary"
            size="sm"
            className="text-xs px-4 py-2"
          >
            Resend
          </ResponsiveButton>
          
          <ResponsiveButton
            onClick={() => onRevoke(invite)}
            variant="danger"
            size="sm"
            className="text-xs px-4 py-2"
          >
            Revoke
          </ResponsiveButton>
        </>
      )}
      
      {invite.status === 'expired' && (
        <ResponsiveButton
          onClick={() => onResend(invite)}
          variant="primary"
          size="sm"
          className="text-xs px-4 py-2"
        >
          Resend
        </ResponsiveButton>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-2 text-gray-600">Loading invites...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Search and Filter Controls */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1">
            <label htmlFor="search" className="sr-only">Search invites</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Search by email..."
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="w-full lg:w-48">
            <label htmlFor="statusFilter" className="sr-only">Filter by status</label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="claimed">Claimed</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>

          {/* Sort Controls */}
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value)}
              className="block px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="created_at">Date Created</option>
              <option value="email">Email</option>
              <option value="status">Status</option>
              <option value="expires_at">Expires</option>
            </select>
            
            <button
              onClick={() => onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              title={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
            >
              <svg 
                className={`h-4 w-4 text-gray-500 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
              </svg>
            </button>
          </div>

          {/* Clear Filters */}
          {(searchTerm || statusFilter) && (
            <ResponsiveButton
              onClick={onClearFilters}
              variant="secondary"
              size="sm"
              className="whitespace-nowrap"
            >
              Clear Filters
            </ResponsiveButton>
          )}
        </div>
      </div>

      {/* Results Count */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
        <p className="text-sm text-gray-700">
          Showing {invites?.length || 0} of {totalCount} invites
        </p>
      </div>

      {/* Invite List */}
      <div className="p-4">
        {!invites || invites.length === 0 ? (
          <div className="text-center py-8">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2M4 13h2"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No invites found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by sending your first invite.'}
            </p>
          </div>
        ) : isMobile ? (
          // Mobile Card View
          <div className="space-y-4">
            {invites.map((invite) => {
              const expiration = formatInviteExpiration(invite.expires_at);
              return (
                <MobileCard key={invite.id} className="p-4">
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {invite.email}
                        </p>
                        <p className="text-xs text-gray-500">
                          Invited by {invite.inviter_name || 'Unknown'}
                        </p>
                      </div>
                      <InviteStatusBadge status={invite.status} />
                    </div>

                    {/* Details */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Created:</span>
                        <span>{formatDate(invite.created_at)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Expires:</span>
                        <span className={expiration.isExpiringSoon ? 'text-orange-600' : expiration.isExpired ? 'text-red-600' : ''}>
                          {expiration.text}
                        </span>
                      </div>
                      {invite.claimed_at && (
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>Claimed:</span>
                          <span>{formatDate(invite.claimed_at)}</span>
                        </div>
                      )}
                      {invite.pre_filled_data && Object.keys(invite.pre_filled_data).length > 0 && (
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>Pre-filled:</span>
                          <span className="text-green-600">Yes</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {getActionButtons(invite)}
                  </div>
                </MobileCard>
              );
            })}
          </div>
        ) : (
          // Desktop Table View
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invited By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pre-filled
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invites.map((invite) => {
                  const expiration = formatInviteExpiration(invite.expires_at);
                  return (
                    <tr key={invite.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {invite.email}
                          </div>
                          {invite.claimed_at && (
                            <div className="text-xs text-gray-500">
                              Claimed: {formatDate(invite.claimed_at)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <InviteStatusBadge status={invite.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invite.inviter_name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(invite.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={expiration.isExpiringSoon ? 'text-orange-600' : expiration.isExpired ? 'text-red-600' : 'text-gray-600'}>
                          {expiration.text}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {invite.pre_filled_data && Object.keys(invite.pre_filled_data).length > 0 ? (
                          <span className="text-green-600">Yes</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {getActionButtons(invite)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center">
            <label htmlFor="pageSize" className="text-sm text-gray-700 mr-2">
              Show:
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700 ml-2">per page</span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteList;